import { useMessageSource } from "react-message-source";
import * as Yup from "yup";
import useEmailValidator from "../shared/components/email/validator";
import {
  useTextInputOptionalValidator,
  useTextInputValidator,
} from "../shared/components/formik/text-input/validator";
import useGenderValidator from "../shared/components/gender/validator";
import useLanguageValidator from "../shared/components/language/validator";
import usePhoneNumberValidator from "../shared/components/phone/validator";
import { useYesNoValidator } from "../shared/components/validator";
import { travelersCountOptions } from "./travelersCountOptions";
import { useAnnualFlightSpendingOptions } from "./useAnnualFlightSpendingOptions";
import { useContext } from "react";
import { ProductContext } from "../../context/product-context/productContext";

export default function useAdditionalBenefitsValidationSchema() {
  const { getMessage } = useMessageSource();

  const { selectedProduct } = useContext(ProductContext);

  const hasPartnerPlusBenefits = selectedProduct?.hasFeature(
    "PARTNER_PLUS_BENEFIT",
  );

  const yesNoValidator = useYesNoValidator();

  const companyTravelersCountValidator = Yup.string()
    .nullable()
    .test(
      "company travelers count validator",
      getMessage("Validation.message-required"),
      (value) => {
        if (value == null) {
          return false;
        }
        return travelersCountOptions
          .map((option) => option.value === value)
          .reduce((val, acc) => acc || val, false);
      },
    );

  const annualFlightSpendingValidator = Yup.string()
    .nullable()
    .oneOf(
      useAnnualFlightSpendingOptions().map((option) => option.value),
      getMessage("Validation.message-required"),
    )
    .required(getMessage("Validation.message-required"));

  const personValidationSchema = Yup.object().shape({
    gender: useGenderValidator(),
    firstName: useTextInputValidator(),
    lastName: useTextInputValidator(),
    companyDepartment: useTextInputOptionalValidator(),
    companyPosition: useTextInputOptionalValidator(),
    mobile: usePhoneNumberValidator(),
    emailBusiness: useEmailValidator(),
    correspondenceLanguage: useLanguageValidator(),
  });

  const partnerPlusBenefitGeneralDtoValidationSchema = Yup.object().shape({
    companyDepartment: useTextInputValidator(),
    companyPosition: useTextInputValidator(),
    companyTravelersCount: companyTravelersCountValidator,
    expectedAnnualFlightSpending: annualFlightSpendingValidator,
  });

  const partnerPlusValidator = Yup.string()
    .required(getMessage("Validation.message-required"))
    .min(6, getMessage("Validation.message-minLengthCharacters", 6))
    .max(16, getMessage("Validation.message-maxLengthCharacters", 16))
    .matches(/^\S+$/, getMessage("Validation.message-mustNotContainSpaces"));

  const partnerPlusFeatureValidator = hasPartnerPlusBenefits
    ? {
        hasLoyalties: yesNoValidator,
        partnerPlusBenefitGeneralDto: Yup.object().when("hasLoyalties", {
          is: "no",
          then: () => partnerPlusBenefitGeneralDtoValidationSchema,
        }),
        plusPartnerUsername: Yup.string()
          .nullable()
          .when("hasLoyalties", {
            is: "yes",
            then: () => partnerPlusValidator,
          }),
        adminContactPerson: Yup.object().when(
          ["hasLoyalties", "isContactPersonSame"],
          {
            is: (hasLoyalties: string, isContactPersonSame: string) =>
              hasLoyalties === "no" && isContactPersonSame === "yes",
            then: () => personValidationSchema,
          },
        ),
        contactPerson: Yup.object().when(
          ["hasLoyalties", "isContactPersonSame"],
          {
            is: "no",
            then: () => personValidationSchema,
          },
        ),
      }
    : {};

  return Yup.object().shape({
    hasCashOnCard: yesNoValidator,
    ...partnerPlusFeatureValidator,
  });
}
